import dayjs from 'dayjs'
import { AggregateHealthPlanComponentData, GeckoUnit } from 'types'

export const criticalityToDaysMap = new Map([
  [1, 1],
  [2, 3],
  [3, 7],
  [4, 14],
  [5, 25],
])

export const getCostPerDay = (
  unit: GeckoUnit | undefined
): number | undefined => {
  const costPerHour = unit?.data?.custom_fields?.find(
    (c) => c.fieldName === CUSTOM_COST_PER_HOUR_DOWN_FIELD
  )?.inputValue

  return costPerHour ? parseInt(costPerHour) * 24 : undefined
}

const CUSTOM_COST_PER_HOUR_DOWN_FIELD = 'cost_per_hour_down'
const MAX_HEALTH_SCORE = 100

// Setting as 0 for now for the general case; use down the line to establish a range of LPO
const GRACE_PERIOD_DAYS = 0

/**
 * Calculates the Loss of Production Opportunity (LPO) based on the provided parameters.
 * @param {Map<string, AggregateHealthPlanComponentData>} assetComponents - A map of asset components with their health plan data.
 * @param {dayjs.Dayjs} year - The year for which the LPO is being calculated.
 * @param {string} criticality - The criticality level, which maps to a number of days.
 * @param {GeckoUnit} [unit] - The raw unit data containing custom fields.
 * @param {number} [minimumHealthScore] - The minimum health score to consider for the calculation.
 * @returns {(number | undefined)} - The calculated LPO or undefined if the necessary data is not available.
 */
export const calculateLPO = ({
  assetComponents,
  year,
  criticality,
  unit,
  minimumHealthScore,
}: {
  assetComponents: Map<string, AggregateHealthPlanComponentData>
  year: dayjs.Dayjs
  criticality: number
  unit?: GeckoUnit
  minimumHealthScore?: number
}): number | undefined => {
  //-------------------------- Extract data and check ---------------------------------
  const numberOfDays = criticalityToDaysMap.get(criticality)

  //default to 0 if no cost per hour down is set
  const costPerDay = getCostPerDay(unit) ?? 0

  const minHealthScore = minimumHealthScore
    ? minimumHealthScore
    : Math.min(
        ...assetComponents
          .values()
          .map(
            (d) =>
              d.preferredPlanDamageAreas?.[year.format('YYYY')]?.healthScore ??
              100
          )
      )

  const yearInputProbability =
    (MAX_HEALTH_SCORE - minHealthScore) / MAX_HEALTH_SCORE

  if (!yearInputProbability || !numberOfDays) {
    return undefined
  }

  //-------------------------- Calculate LPO ---------------------------------
  const numberOfDaysDelta = numberOfDays - GRACE_PERIOD_DAYS
  const lpo = Math.min(yearInputProbability, 1) * costPerDay * numberOfDaysDelta

  return lpo
}
